import { createReducer } from 'redux-starter-kit';
import { selectRequest } from '../../../domain/requests/RequestReducer';
import {
  fetchVehicles,
  editVehicle,
  cancelEditVehicle,
  fetchAddVehicle,
  fetchVehicleDetailsPageData,
  editVehicleDamage,
  cancelEditVehicleDamage,
  fetchVehicleDamages,
  fetchUpdateVehicleDamages,
  retireVehicle,
  cancelRetireVehicle,
  fetchRetireVehicle,
  cancelReinstateVehicle,
  reinstateVehicle,
  fetchReinstateVehicle,
  fetchVehicleUsage,
  fetchUpdateVehicle,
} from './vehicleUiActions';
import { selectModelTypeMap } from '../../../domain/modelTypes/modelTypeReducer';
import { selectVehicleDiagramMap } from '../../../domain/vehicleDiagrams/vehicleDiagramReducer';


/**
 * A selector that returns the state of the 'FETCH_VEHICLES' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 * @example const vehiclesRequest = selectVehiclesRequest(state);
 */
export const selectVehicleDetailsPageDataRequest = (state) => {
  const requestId = state.via.ui.vehicles.requestVehicleId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector that returns the state of the 'FETCH_VEHICLE_DIAGRAMS' action.
 * @param {state} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const vehicleDiagramsRequest = selectVehicleDiagramsRequest(state);
 */
export const selectVehicleDiagramsRequest = (state) => {
  const requestId = state.via.ui.vehicles.requestVehicleDiagramsId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector for the vehicle diagrams loaded by the Vehicle Configuration screen.
 * @param {state} state The redux state for the application.
 * @returns The vehicle diagrams that were loaded.
 * @example const vehicleDiagrams = selectVehicleDiagrams(state);
 */
export const selectVehicleDiagrams = (state) => {
  const vehicleDiagramMap = selectVehicleDiagramMap(state);
  const vehicleDiagrams = Object.values(vehicleDiagramMap);

  return vehicleDiagrams || [];
};


/**
 * A selector that returns the state of the 'FETCH_MODEL_TYPES' action.
 * @param {state} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const modelTypesRequest = selectModelTypesRequest(state);
 */
export const selectModelTypesRequest = (state) => {
  const requestId = state.via.ui.vehicles.requestModelTypesId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector for the model types loaded by the Damage Area Configuration screen.
 * @param {state} state The redux state for the application.
 * @returns The model types that were loaded.
 * @example const modelTypes = selectModelTypes(state);
 */
export const selectModelTypes = (state) => {
  const modelTypeMap = selectModelTypeMap(state);
  const modelTypes = Object.values(modelTypeMap);

  return modelTypes || [];
};

/**
 * A selector for finding the current edit state.
 * @param {object} state The redux state for the application.
 * @returns An object containing details about the vehicle being edited.
 * @example const editState = selectEditState(state);
 */
export const selectEditState = state => state.via.ui.vehicles.editState;

/**
 * A selector that returns the state of the 'FETCH_VEHICLES' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 * @example const vehiclesRequest = selectVehiclesRequest(state);
 */
export const selectVehiclesRequest = (state) => {
  const requestId = state.via.ui.vehicles.requestVehicleId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector that returns the state of the 'FETCH_VEHICLE_DAMAGES' action.
 * @param {state} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const vehicleDamagesRequest = selectVehicleDamagesRequest(state);
 */
export const selectVehicleDamagesRequest = (state) => {
  const requestId = state.via.ui.vehicles.requestVehicleDamagesId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};
/**
 * A selector that returns the state of the 'FETCH_VEHICLE_USAGE' action.
 * @param {state} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const vehicleUsageRequest = selectVehicleUsageRequest(state);
 */
export const selectVehicleUsageRequest = (state) => {
  const requestId = state.via.ui.vehicles.requestVehicleUsageId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};
/**
 * A selector for the current create or update vehicle requests.
 * @param {object} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const addDamageTypesRequest = selectSubmitVehicleDetailRequest(state);
 */
export const selectSubmitVehicleDetailRequest = (state) => {
  const requestId = state.via.ui.vehicles.editState.requestSubmitVehicleId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector for the current 'FETCH_UPDATE_VEHICLE_DAMAGES' request.
 * @param {object} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const addDamageTypesRequest = selectAddDamageTypeRequest(state);
 */
export const selectSubmitVehicleDamagesRequest = (state) => {
  const requestId = state.via.ui.vehicles.editState.requestSubmitVehicleDamagesId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector for the current 'FETCH_UPDATE_VEHICLE_DAMAGES' request.
 * @param {object} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const addDamageTypesRequest = selectAddDamageTypeRequest(state);
 */
export const selectRetireVehicleRequest = (state) => {
  const requestId = state.via.ui.vehicles.editState.requestRetireVehicleId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};
/**
 * A selector for the current 'FETCH_REINSTATE_VEHICLE_' request.
 * @param {object} state The redux state for the application.
 * @returns An object representing the request state.
 */
export const selectReinstateVehicleRequest = (state) => {
  const requestId = state.via.ui.vehicles.editState.requestReinstateVehicleId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

const intitialEditingState = {
  isEditing: false,
  vehicleRegNo: null,
  requestSubmitVehicleId: null,
  showPopConfirm: false,
  requestVehicleId: null,
  isEditingVehicleDamage: false,
  requestVehicleDamagesId: null,
};

const vehicleInitialState = {
  editState: intitialEditingState, // Is a vehicle being edited/created.
  requestVehicleId: null, // The request id for loading vehicles.
};

/**
 * A reducer for handling the UI state changes for the Vehicle Configuration screen.
 */
const vehicleReducer = createReducer(vehicleInitialState, {
  // The user has started editing the vehicle.
  [editVehicle]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      isEditing: true,
      vehicleId: action.payload,
      showPopConfirm: false,
    },
  }),

  // The user has stopped editing the vehicle.
  [cancelEditVehicle]: state => ({
    ...state,
    editState: {
      ...intitialEditingState,
    },
  }),

  // A new request has been made to get vehicles from the server
  [fetchVehicles]: (state, action) => ({
    ...state,
    requestVehicleId: action.payload,
  }),

  // A new request has been made to add a vehicle
  [fetchAddVehicle]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestSubmitVehicleId: action.payload,
    },
  }),

  // A new request has been made to update a vehicle
  [fetchUpdateVehicle]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestSubmitVehicleId: action.payload,
    },
  }),

  // A new requst has been made to add a vehicle
  [fetchVehicleDetailsPageData]: (state, action) => ({
    ...state,
    requestVehicleId: action.payload,
  }),

  // The user has stopped repairing the damage against the vehicle.
  [cancelEditVehicleDamage]: state => ({
    ...state,
    editState: {
      ...intitialEditingState,
    },
  }),

  // The user has started repairing the damage against the vehicle.
  [editVehicleDamage]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      isEditing: false,
      vehicleId: action.payload,
      showPopConfirm: false,
      isEditingVehicleDamage: true,
    },
  }),


  // A new request has been made to get the vehicle damage items from the server
  [fetchVehicleDamages]: (state, action) => ({
    ...state,
    requestVehicleDamagesId: action.payload,
  }),

  // A new requst has been made to refresh the page
  [fetchUpdateVehicleDamages]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestSubmitVehicleDamagesId: action.payload,
    },
  }),

  // Retire Vehicle popup to be displayed
  [retireVehicle]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      retiringVehicle: true,
      vehicleId: action.payload,
    },
  }),

  // Retire vehicle popup to be hidden
  [cancelRetireVehicle]: (state, action) => ({
    ...state,
    editState: {
      ...intitialEditingState,
    },
  }),

  // A new requst has been made to refresh the page
  [fetchRetireVehicle]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestRetireVehicleId: action.payload,
    },
  }),

  [fetchVehicleUsage]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestVehicleUsageId: action.payload,
    },
  }),
  // Reinstate Vehicle popup to be displayed
  [reinstateVehicle]: (state, action) => {
    return ({
      ...state,
      editState: {
        ...state.editState,
        reinstatingVehicle: true,
        vehicleId: action.payload,
      },
    });
  },

  [cancelReinstateVehicle]: (state, action) => ({
    ...state,
    editState: {
      ...intitialEditingState,
    },
  }),

  [fetchReinstateVehicle]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestReinstateVehicleId: action.payload,
    },
  }),
  
});

export default vehicleReducer;
